import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import { BankIcon, Search01Icon } from '@hugeicons/core-free-icons';
import banksData from '../config/banks.json';

const BankAutocomplete = ({ value, onChange, placeholder = "Search or type bank name" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value && value.length > 0) {
      const search = value.toLowerCase();
      const filtered = banksData.filter(bank => 
        bank.name.toLowerCase().includes(search)
      ).slice(0, 6); // Show top 6 matches
      setFilteredBanks(filtered);
    } else {
      setFilteredBanks(banksData.slice(0, 6)); // Show first 6 by default
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.ref?.contains(event.target)) {
        // Fallback for ref naming if needed, but standard logic:
        if (!containerRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-muted-gray">
          <HugeiconsIcon icon={BankIcon} size={18} />
        </div>
        <input
          required
          type="text"
          value={value}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-11 pr-4 py-4 bg-off-white dark:bg-gray-800 border border-light-gray/20 dark:border-gray-700 rounded-2xl focus:ring-2 focus:ring-soft-green outline-none font-bold text-charcoal dark:text-white transition-all shadow-inner"
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 5, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute z-50 left-0 right-0 bg-white dark:bg-dark-surface border border-light-gray/20 dark:border-dark-border shadow-float-lg rounded-2xl overflow-hidden max-h-64 overflow-y-auto custom-scrollbar"
          >
            <div className="p-2 border-b border-light-gray/10 bg-off-white/30 dark:bg-gray-800/30">
              <p className="text-[8px] font-black text-muted-gray uppercase tracking-[0.2em] px-2">Suggestions</p>
            </div>
            {filteredBanks.length > 0 ? (
              filteredBanks.map((bank) => (
                <button
                  key={bank.name}
                  type="button"
                  onClick={() => {
                    onChange(bank.name);
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-soft-green/10 flex items-center justify-between group transition-colors"
                >
                  <div>
                    <p className="font-bold text-sm text-charcoal dark:text-white group-hover:text-soft-green transition-colors">{bank.name}</p>
                    <p className="text-[10px] text-muted-gray font-medium">{bank.category}</p>
                  </div>
                  <HugeiconsIcon icon={Search01Icon} size={14} className="text-muted-gray opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="text-xs font-bold text-muted-gray italic">Keep typing to add "{value}"</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BankAutocomplete;
